import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { showGlobalLoader, getLoaderShownAt } from '../globalLoader';

const PageReadyContext = createContext(null);

const MIN_LOADER_MS = 3000;
const MAX_HERO_WAIT_MS = 15000;

export const PageReadyProvider = ({ children, initialLoadComplete }) => {
  const location = useLocation();
  const [heroReady, setHeroReady] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const generationRef = useRef(0);
  const initialLoaderHiddenRef = useRef(false);

  const pageReady = heroReady && minTimeElapsed;

  const beginPageTransition = useCallback(() => {
    generationRef.current += 1;
    setHeroReady(false);
  }, []);

  const signalHeroReady = useCallback(() => {
    setHeroReady(true);
  }, []);

  const getGeneration = useCallback(() => generationRef.current, []);

  useEffect(() => {
    beginPageTransition();
    setMinTimeElapsed(false);
    const timer = window.setTimeout(() => setMinTimeElapsed(true), MIN_LOADER_MS);
    return () => window.clearTimeout(timer);
  }, [location.pathname, beginPageTransition]);

  useEffect(() => {
    if (heroReady) return undefined;
    const timer = window.setTimeout(() => setHeroReady(true), MAX_HERO_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [location.pathname, heroReady]);

  useEffect(() => {
    if (initialLoaderHiddenRef.current) return undefined;
    if (!initialLoadComplete || !heroReady) return undefined;

    const elapsed = Date.now() - getLoaderShownAt();
    const remaining = Math.max(0, MIN_LOADER_MS - elapsed);
    const timer = window.setTimeout(() => {
      initialLoaderHiddenRef.current = true;
      showGlobalLoader(false);
    }, remaining);

    return () => window.clearTimeout(timer);
  }, [initialLoadComplete, heroReady]);

  const value = {
    heroReady,
    pageReady,
    signalHeroReady,
    getGeneration,
  };

  return (
    <PageReadyContext.Provider value={value}>
      {children}
    </PageReadyContext.Provider>
  );
};

export const usePageReady = () => {
  const ctx = useContext(PageReadyContext);
  if (!ctx) {
    return {
      heroReady: true,
      pageReady: true,
      signalHeroReady: () => {},
      getGeneration: () => 0,
    };
  }
  return ctx;
};

/** Pages without an image hero should call this so navigation can finish. */
export const useMarkPageReadyWithoutHero = () => {
  const { signalHeroReady } = usePageReady();
  useEffect(() => {
    signalHeroReady();
  }, [signalHeroReady]);
};
